import { useCallback, useEffect, useRef, useState } from "react";
import type Hls from "hls.js";
import {
  AlertCircle,
  Loader2,
  Maximize,
  Minimize,
  Pause,
  Play,
  Volume2,
  VolumeX,
} from "lucide-react";
import { api } from "@/lib/api.js";
import type { CapituloPublico, ConfigPlayer, CtaPublico } from "@/lib/videos.js";

/**
 * O player do Academy. Trazido do HackStudio
 * (`src/components/videos/player-video.tsx`), adaptado do Next para o Vite.
 *
 * Nosso, e não o iframe da Cloudflare, porque é aqui que mora o que importa
 * numa aula: legenda, capítulos, retomada, marca d'água com o e-mail de quem
 * assiste — e a medição que alimenta o progresso do aluno sem ele precisar
 * arrastar barra nenhuma.
 *
 * Dados, manifesto e sessão passam pelo cliente `api` (com o token do aluno).
 * O batimento não: ele sai por `sendBeacon` quando a aba fecha, que não manda
 * cabeçalho — o código opaco da sessão é o que o autentica.
 */

const BASE = "/api/videos";

interface DadosVideo {
  titulo: string;
  capa_url: string | null;
  duracao_seg: number | null;
  config: ConfigPlayer;
  ctas: CtaPublico[];
  capitulos: CapituloPublico[];
  /** E-mail de quem assiste, para a marca d'água. */
  espectador: string;
  /** Idiomas com legenda pronta: `pt-BR`, `en`, `es`. */
  legendas: string[];
}

const ROTULO_IDIOMA: Record<string, string> = {
  "pt-BR": "Português",
  en: "Inglês",
  es: "Espanhol",
};

/**
 * A curva da barra fictícia.
 *
 * `exibido = real^0.6` faz a barra andar mais depressa no começo: aos 25% reais
 * ela mostra 43%, e na metade mostra 66%. Quem chega no meio de uma VSL vê uma
 * barra quase cheia e fica — que é exatamente o que o recurso existe para
 * provocar, e por isso a tela de edição o chama de fictícia e não de outra
 * coisa.
 *
 * O expoente também precisa saber voltar: sem a inversa, arrastar a barra
 * levaria para um segundo diferente do que o dedo apontou.
 */
const EXPOENTE = 0.6;
const paraExibicao = (real: number) => (real <= 0 ? 0 : Math.pow(Math.min(real, 1), EXPOENTE));
const paraReal = (exibido: number) => (exibido <= 0 ? 0 : Math.pow(Math.min(exibido, 1), 1 / EXPOENTE));

const VELOCIDADES = [0.75, 1, 1.25, 1.5, 2];

/** A mensagem que a API mandou, ou a de reserva. */
function mensagemDeErro(e: unknown, reserva: string) {
  const r = (e as { response?: { data?: { error?: { message?: string } } } })?.response;
  return r?.data?.error?.message ?? reserva;
}

function relogio(segundos: number) {
  if (!Number.isFinite(segundos) || segundos < 0) return "0:00";
  const m = Math.floor(segundos / 60);
  const s = Math.floor(segundos % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** Onde o ponto de parada de cada vídeo fica guardado, por espectador. */
const chaveRetomada = (codigo: string) => `ha-video-pos-${codigo}`;

/** Tempo de observação depois do play. Vide `tentarTocar`. */
const OLHAR_DE_NOVO_MS = 600;

/** Espera um evento do vídeo, ou desiste. Devolve se ele chegou a acontecer. */
function esperarEvento(elemento: HTMLVideoElement, evento: string, limiteMs: number) {
  return new Promise<boolean>((resolve) => {
    const chegou = () => encerrar(true);
    const relogio = setTimeout(() => encerrar(false), limiteMs);
    function encerrar(aconteceu: boolean) {
      clearTimeout(relogio);
      elemento.removeEventListener(evento, chegou);
      resolve(aconteceu);
    }
    elemento.addEventListener(evento, chegou);
  });
}

/**
 * Espera o vídeo ter o que tocar.
 *
 * `play()` logo depois de trocar o `src` briga com o carregamento que acabou de
 * começar: a chamada é abortada, o erro vem como `AbortError` — que a gente
 * engolia — e sobra um vídeo parado no primeiro quadro, mudo, sem botão nenhum.
 * Era o que acontecia ao abrir a bolha. Esperar os metadados custa alguns
 * milissegundos e é a diferença entre tocar e não tocar.
 */
async function esperarPronto(elemento: HTMLVideoElement) {
  if (elemento.readyState >= 1) return;
  await esperarEvento(elemento, "loadedmetadata", 8000);
}

/**
 * Toca; se o navegador recusar o som, toca mudo.
 *
 * A bolha no canto do site abre por um clique na PÁGINA que incorpora, e não
 * dentro do iframe — e cada navegador julga esse gesto de um jeito diferente.
 * Sem a queda para mudo sobrava o pior resultado possível: o player já se
 * considerava iniciado e o vídeo ficava parado no primeiro quadro, sem botão
 * nenhum para tocá-lo.
 *
 * O jeito ÓBVIO de detectar a recusa não funciona. Medido em produção, com
 * clique de gente de verdade:
 *
 *     com som: play() RESOLVEU, pausado=false, t=0,04
 *     800 ms depois: pausado=true, t=0,04
 *
 * O Chrome não rejeita a promessa — ele deixa o play resolver e pausa logo
 * depois. Um `catch` nunca chega a rodar, e foi por isso que a primeira versão
 * disto passou nos testes e falhou no site. Só olhando de novo, um instante
 * adiante, dá para saber que o som foi barrado.
 *
 * Devolve se o som sobreviveu; quem chama acende o convite "toque para ouvir".
 */
async function tentarTocar(elemento: HTMLVideoElement, comSom: boolean) {
  elemento.muted = !comSom;

  const cairParaMudo = async () => {
    elemento.muted = true;
    await elemento.play().catch(() => undefined);
    return false;
  };

  try {
    await elemento.play();
  } catch {
    return comSom ? cairParaMudo() : false;
  }
  if (!comSom) return false;

  /* Pausar sozinho logo depois de começar é a recusa silenciosa. Gente que
     aperta pausa dentro de 600 ms do play é caso que não acontece, e o preço se
     acontecesse seria só o vídeo voltar a tocar mudo. */
  if (elemento.paused) return cairParaMudo();
  const pausouSozinho = await esperarEvento(elemento, "pause", OLHAR_DE_NOVO_MS);
  return pausouSozinho ? cairParaMudo() : true;
}

export function PlayerVideo({
  codigo,
  aulaId,
  autoIniciar,
}: {
  codigo: string;
  /** A aula em que o vídeo está tocando: o batimento alimenta o progresso dela. */
  aulaId?: string;
  /**
   * Começar sozinho, com som, porque quem abriu já clicou.
   *
   * É a bolha do canto do site: o clique que a expande é o play. Diferente do
   * `config.autoplay` do vídeo, que é decisão de peça (VSL) e começa mudo.
   */
  autoIniciar?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const barraRef = useRef<HTMLDivElement>(null);
  const hlsRef = useRef<Hls | null>(null);

  const [dados, setDados] = useState<DadosVideo | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [iniciado, setIniciado] = useState(false);
  const [tocando, setTocando] = useState(false);
  const [mudo, setMudo] = useState(false);
  const [agora, setAgora] = useState(0);
  const [duracao, setDuracao] = useState(0);
  const [velocidade, setVelocidade] = useState(1);
  const [telaCheia, setTelaCheia] = useState(false);
  const [miniaturas, setMiniaturas] = useState<string | null>(null);
  const [baseLegendas, setBaseLegendas] = useState<string | null>(null);
  const [previa, setPrevia] = useState<{ tempo: number; x: number } | null>(null);
  const [marca, setMarca] = useState({ topo: 12, esquerda: 8 });

  /*
   * A medição vive em refs, não em estado.
   *
   * `timeupdate` dispara umas quatro vezes por segundo; guardar o balde
   * assistido em estado redesenharia o player inteiro a cada disparo, para
   * mostrar exatamente a mesma coisa. Nada disto aparece na tela.
   */
  /* Marcos já anunciados à página que incorpora, para o pixel não disparar
     duas vezes o mesmo evento — no Meta isso vira duas conversões. */
  const marcosAvisados = useRef<Set<number>>(new Set());
  const sessaoRef = useRef<string | null>(null);
  const baldesPendentes = useRef<Set<number>>(new Set());
  /* Tudo que esta sessão já reportou. Sem esta memória, quem revê um trecho o
     contaria de novo, e a curva de retenção passaria de 100% — o gráfico
     mostraria mais gente assistindo o meio do que gente que apertou play. */
  const baldesEnviados = useRef<Set<number>>(new Set());
  const segundoRef = useRef(0);

  const config = dados?.config ?? {};
  const mostrarControles = config.controles !== false;

  /* ──────────────────────────── medição ──────────────────────────────── */

  /**
   * Avisa a página que incorpora o vídeo.
   *
   * É assim que o marco de reprodução vira pixel. O pixel do cliente mora na
   * página DELE; aqui dentro é um iframe de outra origem, e nada que a gente
   * dispare deste lado chega ao Meta dele. O player conta o que aconteceu, e
   * quem tem o pixel decide o que fazer — que é a única divisão possível
   * quando o embed é iframe.
   *
   * `postMessage` para `*` porque o destino é o site do cliente, que muda a
   * cada instalação. Não vai segredo nenhum na mensagem: é o código público do
   * vídeo e um percentual.
   */
  const avisarPagina = useCallback(
    (evento: string, dados: Record<string, unknown> = {}) => {
      if (typeof window === "undefined" || window.parent === window) return;
      window.parent.postMessage(
        { type: "hackacademy-video", evento, codigo, ...dados },
        "*",
      );
    },
    [codigo],
  );

  /**
   * Manda o que foi assistido desde o último envio.
   *
   * `sendBeacon` no caminho de saída: quando a aba fecha, um `fetch` é
   * cancelado junto com a página e o último trecho — justamente o que diz onde
   * a pessoa desistiu — se perde. O `sendBeacon` é entregue pelo navegador
   * depois de a página morrer, que é a única forma de medir o fim de uma
   * sessão.
   */
  const enviarBatimento = useCallback(
    (saindo = false) => {
      const sessao = sessaoRef.current;
      if (!sessao) return;

      const baldes = [...baldesPendentes.current].filter((b) => !baldesEnviados.current.has(b));
      baldesPendentes.current.clear();
      // Sem trecho novo, só vale mandar na saída — para registrar onde parou.
      if (baldes.length === 0 && !saindo) return;
      for (const b of baldes) baldesEnviados.current.add(b);

      const corpo = JSON.stringify({ sessao, segundo: Math.floor(segundoRef.current), baldes });
      const url = `${BASE}/${encodeURIComponent(codigo)}/beacon`;

      if (saindo && typeof navigator.sendBeacon === "function") {
        /* `text/plain` e não `application/json`: o `sendBeacon` não faz
           preflight, e o segundo tipo exigiria um — o navegador bloquearia o
           batimento de saída antes de ele sair. O servidor tem um parser para
           este caso, em `app.ts`. */
        navigator.sendBeacon(url, new Blob([corpo], { type: "text/plain;charset=UTF-8" }));
        return;
      }
      void fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: corpo,
        keepalive: true,
      }).catch(() => undefined);
    },
    [codigo],
  );

  /* Um envio a cada cinco segundos: é a resolução do balde, e mais frequente
     seria pedido de rede sem informação nova. */
  useEffect(() => {
    if (!iniciado) return;
    const t = setInterval(() => enviarBatimento(false), 5000);
    return () => clearInterval(t);
  }, [iniciado, enviarBatimento]);

  /* `pagehide` e não `beforeunload`: no Safari do iPhone o segundo simplesmente
     não dispara, e é lá que boa parte de uma VSL é assistida. */
  useEffect(() => {
    const aoSair = () => enviarBatimento(true);
    window.addEventListener("pagehide", aoSair);
    return () => {
      window.removeEventListener("pagehide", aoSair);
      // Desmontar também é sair: numa área de membros, trocar de aula não
      // dispara `pagehide` nenhum.
      aoSair();
    };
  }, [enviarBatimento]);

  /* ─────────────────────────── carga inicial ─────────────────────────── */

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const { data } = await api.get<DadosVideo>(`/videos/${encodeURIComponent(codigo)}`);
        if (vivo) setDados(data);
      } catch (e) {
        if (vivo) setErro(mensagemDeErro(e, "Vídeo indisponível."));
      } finally {
        if (vivo) setCarregando(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [codigo]);

  /*
   * O hls.js segura um worker e um buffer de vídeo. Sem destruir no desmonte,
   * fechar a aula continua baixando segmentos de um vídeo que ninguém está
   * vendo — e numa área de membros, cada aula aberta deixa mais um para trás.
   */
  useEffect(() => {
    return () => {
      hlsRef.current?.destroy();
      hlsRef.current = null;
    };
  }, []);

  /* ──────────────────────────── reprodução ───────────────────────────── */

  const comecar = useCallback(
    async (comSom: boolean) => {
      const elemento = videoRef.current;
      if (!elemento || iniciado) return;
      setIniciado(true);
      /* Quem decide o mudo de verdade é `tentarTocar`, que sabe se o navegador
         deixou o som passar. Aqui fica só a intenção, para a tela não piscar. */
      setMudo(!comSom);

      try {
        let corpo: { url: string; miniaturas: string; legendas: string };
        try {
          corpo = (await api.get(`/videos/${encodeURIComponent(codigo)}/manifesto`)).data;
        } catch (e) {
          setErro(mensagemDeErro(e, "Este vídeo não pode ser reproduzido aqui."));
          setIniciado(false);
          return;
        }
        setMiniaturas(corpo.miniaturas);
        setBaseLegendas(corpo.legendas);
        const url = corpo.url;

        /*
         * A sessão abre aqui, no play, e não no carregamento da página: uma VSL
         * numa aba esquecida contaria como audiência sem ninguém ter assistido,
         * e a curva de retenção começaria mentindo na base.
         */
        void api
          .post<{ sessao: string }>(`/videos/${encodeURIComponent(codigo)}/sessao`, {
            aula_id: aulaId,
            fuso: Intl.DateTimeFormat().resolvedOptions().timeZone ?? "",
          })
          .then(({ data }) => {
            if (data?.sessao) sessaoRef.current = data.sessao;
          })
          .catch(() => undefined);

        /*
         * O Safari toca HLS sozinho; Chrome e Firefox não. Testar o suporte
         * nativo primeiro evita carregar 400 KB de biblioteca para quem não
         * precisa dela — e no iOS o caminho nativo é o único que entrega tela
         * cheia e Picture-in-Picture de verdade.
         */
        if (elemento.canPlayType("application/vnd.apple.mpegurl")) {
          elemento.src = url;
          await esperarPronto(elemento);
          setMudo(!(await tentarTocar(elemento, comSom)));
          return;
        }

        const { default: Hls } = await import("hls.js");
        if (!Hls.isSupported()) {
          setErro("Este navegador não consegue reproduzir o vídeo.");
          setIniciado(false);
          return;
        }

        const hls = new Hls({ enableWorker: true });
        hlsRef.current = hls;
        hls.on(Hls.Events.ERROR, (_e, dado) => {
          // Só o erro fatal vira mensagem: o hls.js se recupera sozinho de falha
          // de segmento, e acusar isso na tela assustaria quem está assistindo
          // um vídeo que não parou.
          if (dado.fatal) setErro("A reprodução foi interrompida.");
        });
        hls.loadSource(url);
        hls.attachMedia(elemento);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          void esperarPronto(elemento)
            .then(() => tentarTocar(elemento, comSom))
            .then((comSomAinda) => setMudo(!comSomAinda));
        });
      } catch {
        setErro("Não foi possível iniciar a reprodução.");
        setIniciado(false);
      }
    },
    [codigo, iniciado, aulaId],
  );

  /*
   * Smart Autoplay: começa mudo assim que a página carrega.
   *
   * Mudo não é detalhe, é o que torna o recurso possível: navegador nenhum
   * deixa um vídeo com som começar sozinho. O movimento é o que chama o clique,
   * e o clique é o que liga o som.
   */
  useEffect(() => {
    if (iniciado || !dados) return;
    /* A bolha começa com som porque o clique dela já foi o play; a VSL começa
       muda porque ninguém pediu nada ainda. */
    if (autoIniciar) void comecar(true);
    else if (dados.config.autoplay) void comecar(false);
  }, [dados, iniciado, comecar, autoIniciar]);

  /* ───────────────────────────── retomada ────────────────────────────── */

  /*
   * Volta para onde parou.
   *
   * Guardado no navegador de quem assiste, e não no nosso banco: na fase 2 não
   * existe sessão nem contato ligado ao play, e inventar um identificador só
   * para isso criaria um registro de quem viu o quê sem que ninguém tivesse
   * pedido. Na fase 3, quando o play virar evento do lead, o ponto de parada
   * passa a ter dono e sobe junto.
   */
  useEffect(() => {
    const elemento = videoRef.current;
    if (!elemento || !iniciado || config.retomar === false || duracao <= 0) return;

    try {
      const salvo = Number(localStorage.getItem(chaveRetomada(codigo)));
      // Perto do fim não se retoma: quem assistiu quase tudo quer rever, e
      // cair a dez segundos do fim parece o vídeo ter quebrado.
      if (Number.isFinite(salvo) && salvo > 15 && salvo < duracao - 30) {
        elemento.currentTime = salvo;
      }
    } catch {
      // Navegador com armazenamento bloqueado: retomar é conveniência, não
      // pode derrubar a reprodução.
    }
    // Só na primeira vez que a duração aparece — depois disso seria pular
    // sozinho no meio de quem já está assistindo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iniciado, duracao > 0]);

  useEffect(() => {
    if (!iniciado || config.retomar === false || agora <= 0) return;
    try {
      localStorage.setItem(chaveRetomada(codigo), String(Math.floor(agora)));
    } catch {
      /* idem */
    }
  }, [agora, iniciado, codigo, config.retomar]);

  /* ──────────────────────────── marca d'água ─────────────────────────── */

  /*
   * A marca anda pela tela a cada oito segundos.
   *
   * Parada, ela é recortada em trinta segundos de edição de vídeo. Andando, o
   * recorte teria de acompanhar — e é isso que faz quem pensa em repassar a
   * aula desistir, que é todo o efeito que ela promete. Não é DRM e o §4.3 não
   * a vende como tal.
   */
  useEffect(() => {
    if (!config.marcaDagua || !iniciado) return;
    const t = setInterval(() => {
      setMarca({ topo: 8 + Math.random() * 78, esquerda: 4 + Math.random() * 62 });
    }, 8000);
    return () => clearInterval(t);
  }, [config.marcaDagua, iniciado]);

  /* ───────────────────────────── controles ───────────────────────────── */

  function alternarPlay() {
    const elemento = videoRef.current;
    if (!elemento) return;
    if (elemento.paused) void elemento.play().catch(() => undefined);
    else elemento.pause();
  }

  function alternarSom() {
    const elemento = videoRef.current;
    if (!elemento) return;
    elemento.muted = !elemento.muted;
    setMudo(elemento.muted);
  }

  function trocarVelocidade() {
    const elemento = videoRef.current;
    if (!elemento) return;
    const proxima = VELOCIDADES[(VELOCIDADES.indexOf(velocidade) + 1) % VELOCIDADES.length]!;
    elemento.playbackRate = proxima;
    setVelocidade(proxima);
  }

  async function alternarTelaCheia() {
    const alvo = containerRef.current;
    if (!alvo) return;
    if (document.fullscreenElement) await document.exitFullscreen().catch(() => undefined);
    else await alvo.requestFullscreen().catch(() => undefined);
  }

  useEffect(() => {
    const aoTrocar = () => setTelaCheia(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", aoTrocar);
    return () => document.removeEventListener("fullscreenchange", aoTrocar);
  }, []);

  /** Onde o dedo tocou na barra, em segundos — desfazendo a curva fictícia. */
  function tempoNaBarra(clientX: number) {
    const barra = barraRef.current;
    if (!barra || duracao <= 0) return 0;
    const caixa = barra.getBoundingClientRect();
    const fracao = Math.min(Math.max((clientX - caixa.left) / caixa.width, 0), 1);
    const real = config.barraFicticia ? paraReal(fracao) : fracao;
    return real * duracao;
  }

  function buscar(clientX: number) {
    const elemento = videoRef.current;
    if (!elemento) return;
    elemento.currentTime = tempoNaBarra(clientX);
  }

  /* ────────────────────────────── desenho ────────────────────────────── */

  if (carregando) {
    return (
      <Moldura>
        <Loader2 className="h-6 w-6 animate-spin text-white/70" />
      </Moldura>
    );
  }

  if (erro) {
    return (
      <Moldura>
        <div className="flex flex-col items-center gap-2 px-6 text-center text-sm text-white/80">
          <AlertCircle className="h-6 w-6" />
          {erro}
        </div>
      </Moldura>
    );
  }

  const cor = config.cor || "#f97316";
  const fracaoReal = duracao > 0 ? agora / duracao : 0;
  const fracaoBarra = config.barraFicticia ? paraExibicao(fracaoReal) : fracaoReal;
  const ctasAgora = (dados?.ctas ?? []).filter(
    (c) => agora >= c.seg_ini && (c.seg_fim === 0 || agora <= c.seg_fim),
  );
  const nomeMarca = dados?.espectador ?? "";

  return (
    <div className="flex h-full w-full flex-col bg-black">
      <div ref={containerRef} className="relative min-h-0 flex-1 bg-black">
        <video
          ref={videoRef}
          className="h-full w-full"
          /*
           * Sem isto a legenda simplesmente não carrega.
           *
           * A faixa vem do domínio de entrega da Cloudflare, e o navegador
           * recusa `<track>` de outra origem num vídeo sem `crossOrigin` — em
           * silêncio, sem erro no console em alguns navegadores. A Cloudflare
           * responde com CORS aberto (é um serviço feito para tocar em domínio
           * de terceiro), então o pedido do vídeo em si não muda.
           */
          crossOrigin="anonymous"
          playsInline
          poster={dados?.capa_url ?? undefined}
          onClick={() => iniciado && alternarPlay()}
          onPlay={() => {
            setTocando(true);
            avisarPagina("play");
          }}
          onPause={() => setTocando(false)}
          onTimeUpdate={(e) => {
            const t = e.currentTarget.currentTime;
            setAgora(t);
            segundoRef.current = t;
            /* Só conta o balde por onde a cabeça de reprodução passou. Deduzir
               pela posição contaria o pulo para o fim como audiência do meio, e
               contaria duas vezes quem revê um trecho. */
            if (!e.currentTarget.paused) {
              const balde = Math.floor(t / 5);
              if (!baldesEnviados.current.has(balde)) baldesPendentes.current.add(balde);
            }

            const total = e.currentTarget.duration || 0;
            if (total > 0) {
              const pct = (t / total) * 100;
              for (const marco of [25, 50, 75, 100]) {
                if (pct >= marco && !marcosAvisados.current.has(marco)) {
                  marcosAvisados.current.add(marco);
                  avisarPagina("marco", { percentual: marco });
                }
              }
            }
          }}
          onLoadedMetadata={(e) => setDuracao(e.currentTarget.duration || 0)}
          onEnded={() => {
            setTocando(false);
            avisarPagina("fim");
            // O fim é o marco que mais diz. Não pode esperar o próximo ciclo.
            enviarBatimento(true);
          }}
        >
          {/* Faixa nativa, e não legenda desenhada por nós: é o que faz o
              seletor do próprio navegador aparecer — inclusive na tela cheia do
              iOS, onde nenhum controle nosso sobrevive. A primeira vira padrão
              só quando há uma; `default` em todas ligaria três ao mesmo tempo. */}
          {baseLegendas &&
            (dados?.legendas ?? []).map((idioma, i) => (
              <track
                key={idioma}
                kind="subtitles"
                srcLang={idioma}
                label={ROTULO_IDIOMA[idioma] ?? idioma}
                src={`${baseLegendas}/${idioma}.vtt`}
                default={i === 0}
              />
            ))}
        </video>

        {config.logoUrl && iniciado && (
          <img
            src={config.logoUrl}
            alt=""
            className="pointer-events-none absolute right-3 top-3 h-8 opacity-80"
          />
        )}

        {config.marcaDagua && iniciado && nomeMarca && (
          <div
            className="pointer-events-none absolute select-none text-[11px] font-medium text-white/35 transition-all duration-1000"
            style={{ top: `${marca.topo}%`, left: `${marca.esquerda}%` }}
          >
            {nomeMarca}
          </div>
        )}

        {/* CTA sobre o vídeo: para a oferta que precisa competir com a imagem. */}
        {ctasAgora
          .filter((c) => c.posicao === "sobre")
          .map((c) => (
            <a
              key={c.id}
              href={c.url}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute bottom-16 left-1/2 -translate-x-1/2 rounded-md px-5 py-2.5 text-sm font-semibold text-white shadow-lg"
              style={{ background: cor }}
            >
              {c.texto}
            </a>
          ))}

        {/* A capa com o botão vive por cima até o primeiro play, e não é o
            `poster` sozinho: sem um alvo grande e óbvio, o toque no celular cai
            no meio do vídeo e não em "tocar". */}
        {!iniciado && (
          <button
            type="button"
            onClick={() => void comecar(true)}
            aria-label={`Reproduzir ${dados?.titulo ?? "vídeo"}`}
            className="absolute inset-0 flex items-center justify-center bg-black/30 transition-colors hover:bg-black/40"
          >
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/90">
              <Play className="ml-1 h-7 w-7 text-black" />
            </span>
          </button>
        )}

        {/* Autoplay mudo pede o som de volta com um alvo do tamanho do vídeo:
            o convite é a razão de o recurso existir, e um ícone pequeno no
            canto perderia a maior parte dos toques no celular. */}
        {iniciado && mudo && (config.autoplay || autoIniciar) && (
          <button
            type="button"
            onClick={alternarSom}
            className="absolute inset-0 flex items-end justify-center bg-transparent pb-24"
          >
            <span className="flex items-center gap-2 rounded-full bg-black/70 px-4 py-2 text-sm font-medium text-white">
              <VolumeX className="h-4 w-4" />
              Toque para ouvir
            </span>
          </button>
        )}

      </div>

      {iniciado && mostrarControles && (
        <div className="shrink-0 bg-black px-3 pb-2 pt-1">
          <div
            ref={barraRef}
            className="group relative h-3 cursor-pointer"
            onClick={(e) => buscar(e.clientX)}
            onMouseMove={(e) => {
              const barra = barraRef.current;
              if (!barra) return;
              setPrevia({
                tempo: tempoNaBarra(e.clientX),
                x: e.clientX - barra.getBoundingClientRect().left,
              });
            }}
            onMouseLeave={() => setPrevia(null)}
          >
            <div className="absolute top-1/2 h-1 w-full -translate-y-1/2 rounded bg-white/25" />
            <div
              className="absolute top-1/2 h-1 -translate-y-1/2 rounded"
              style={{ width: `${fracaoBarra * 100}%`, background: cor }}
            />
            {/* Marcadores de capítulo. Na barra fictícia eles andam junto com
                ela — um marcador no lugar "certo" denunciaria a curva. */}
            {/* A prévia mora DENTRO da barra: o `x` é medido em relação a ela,
                e no contêiner do vídeo o padding dos controles o desalinharia. */}
            {previa && miniaturas && (
              <div
                className="pointer-events-none absolute bottom-full mb-2 -translate-x-1/2 overflow-hidden rounded border border-white/20"
                style={{ left: previa.x }}
              >
                <img
                  src={`${miniaturas}?time=${Math.floor(previa.tempo)}s&height=90`}
                  alt=""
                  className="block h-[90px] w-auto"
                />
                <div className="bg-black/80 py-0.5 text-center text-[10px] text-white">
                  {relogio(previa.tempo)}
                </div>
              </div>
            )}
            {duracao > 0 &&
              (dados?.capitulos ?? []).map((c) => {
                const real = c.segundo / duracao;
                const pos = config.barraFicticia ? paraExibicao(real) : real;
                return (
                  <span
                    key={c.id}
                    title={c.titulo}
                    className="absolute top-1/2 h-2.5 w-0.5 -translate-y-1/2 bg-white/70"
                    style={{ left: `${pos * 100}%` }}
                  />
                );
              })}
          </div>

          <div className="mt-1 flex items-center gap-3 text-white">
            <button onClick={alternarPlay} aria-label={tocando ? "Pausar" : "Reproduzir"}>
              {tocando ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
            </button>
            <button onClick={alternarSom} aria-label={mudo ? "Ativar som" : "Silenciar"}>
              {mudo ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
            </button>
            <span className="text-xs tabular-nums text-white/80">
              {relogio(agora)} / {relogio(duracao)}
            </span>
            <span className="flex-1" />
            {config.velocidade !== false && (
              <button
                onClick={trocarVelocidade}
                className="text-xs font-medium text-white/80"
                aria-label="Velocidade de reprodução"
              >
                {velocidade}×
              </button>
            )}
            <button onClick={() => void alternarTelaCheia()} aria-label="Tela cheia">
              {telaCheia ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
            </button>
          </div>
        </div>
      )}

      {/* CTA na faixa: fica abaixo do vídeo, sem tapar a imagem. É o padrão
          para curso, onde cobrir a aula com um botão atrapalha quem estuda. */}
      {ctasAgora.some((c) => c.posicao === "abaixo") && (
        <div className="flex shrink-0 flex-wrap gap-2 bg-black px-3 pb-3">
          {ctasAgora
            .filter((c) => c.posicao === "abaixo")
            .map((c) => (
              <a
                key={c.id}
                href={c.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 rounded-md px-4 py-2 text-center text-sm font-semibold text-white"
                style={{ background: cor }}
              >
                {c.texto}
              </a>
            ))}
        </div>
      )}
    </div>
  );
}

function Moldura({ children }: { children: React.ReactNode }) {
  return <div className="flex h-full w-full items-center justify-center bg-black">{children}</div>;
}
